import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Spinner } from "@/components/ui/LoadingStates";
import { Button } from "@/components/ui/button";
import { ShieldAlert } from "lucide-react";

export type DocKey = "architecture" | "features" | "qa";

interface Props {
  docKey: DocKey;
  children: React.ReactNode;
}

/**
 * Route guard for internal documentation pages.
 * - Logged-out users → redirected to /auth
 * - Authenticated users without permission → "Access Restricted" screen
 * - Super admins → always allowed
 * - Other users → allowed only if `documentation_access` grants the specific doc
 *
 * Children are NEVER rendered until the permission check completes.
 */
export const DocsAccessRoute: React.FC<Props> = ({ docKey, children }) => {
  const { user, loading } = useAuth();
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (loading) return;
    if (!user) {
      setChecking(false);
      return;
    }

    setChecking(true);
    supabase
      .rpc("user_can_access_doc", { p_user_id: user.id, p_doc_key: docKey })
      .then(({ data, error }) => {
        if (cancelled) return;
        setAllowed(!error && Boolean(data));
        setChecking(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.id, loading, docKey]);

  if (loading || checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Spinner size="lg" text="Verifying access…" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (!allowed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="max-w-md text-center space-y-6">
          <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <ShieldAlert className="w-8 h-8 text-destructive" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold">Access Restricted</h1>
            <p className="text-muted-foreground">
              You don't have permission to view this internal documentation. If you
              believe this is a mistake, please contact a super administrator.
            </p>
          </div>
          <div className="flex gap-3 justify-center">
            <Button asChild variant="outline">
              <Link to="/">Go home</Link>
            </Button>
            <Button asChild>
              <Link to="/profile">Open profile</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default DocsAccessRoute;
